import { useContext, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  EmailAuthProvider,
  reauthenticateWithCredential,
  sendEmailVerification,
  updatePassword,
  verifyBeforeUpdateEmail,
} from 'firebase/auth';
import { doc, updateDoc } from 'firebase/firestore';
import { AlertTriangle, CheckCircle2, KeyRound, Loader2, Mail, ShieldCheck } from 'lucide-react';
import { toast } from 'sonner';
import { AuthContext } from '../../contexts/AuthContext';
import { db } from '../../firebase/config';
import { handleFirebaseError } from '../../utils/errorHandler';
import { hasRealEmail, LEGACY_EMAIL_DOMAIN } from '../../utils/accountStatus';
import { Button } from '../ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';

const Section = ({ icon: Icon, kicker, title, description, children }) => (
  <Card className="border-border/60 bg-background/40">
    <CardHeader>
      <div className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.3em] text-muted-foreground">
        {Icon && <Icon className="h-3 w-3" />}
        {kicker}
      </div>
      <CardTitle className="font-display text-lg">{title}</CardTitle>
      {description && <CardDescription>{description}</CardDescription>}
    </CardHeader>
    <CardContent>{children}</CardContent>
  </Card>
);

const EmailSection = () => {
  const { currentUser, userProfile } = useContext(AuthContext);
  const [newEmail, setNewEmail] = useState('');
  const [currentPassword, setCurrentPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const realEmail = hasRealEmail(userProfile, currentUser);
  const emailVerified = Boolean(currentUser?.emailVerified);

  const handleUpdate = async (e) => {
    e.preventDefault();
    setError('');
    const target = newEmail.trim();
    if (!target) return setError('Enter a new email address.');
    if (!currentPassword) return setError('Enter your current password to confirm.');

    setLoading(true);
    try {
      const signInEmail = currentUser.email;
      if (!signInEmail) throw new Error('Account is missing a sign-in email.');
      const credential = EmailAuthProvider.credential(signInEmail, currentPassword);
      await reauthenticateWithCredential(currentUser, credential);

      await verifyBeforeUpdateEmail(currentUser, target);

      await updateDoc(doc(db, 'userProfiles', currentUser.uid), {
        email: target,
        hasEmail: true,
        updatedAt: new Date().toISOString(),
      });

      setNewEmail('');
      setCurrentPassword('');
      toast.success('Verification email sent.', {
        description: `Click the link in ${target} to confirm your new address.`,
      });
    } catch (err) {
      console.error(err);
      setError(handleFirebaseError(err));
    } finally {
      setLoading(false);
    }
  };

  const resendVerification = async () => {
    try {
      await sendEmailVerification(currentUser);
      toast.success('Verification email sent.');
    } catch (err) {
      toast.error('Could not send verification.', { description: handleFirebaseError(err) });
    }
  };

  return (
    <Section
      icon={Mail}
      kicker="Email"
      title={realEmail ? 'Email address' : 'Add your email address'}
      description={
        realEmail
          ? 'Your sign-in email. Changes require verifying the new address.'
          : "You signed up without an email. Add one now so you don't lose access to your account."
      }
    >
      <div className="flex flex-col gap-5">
        {realEmail ? (
          <div className="flex flex-wrap items-center justify-between gap-3 rounded-md border border-border/60 bg-card px-4 py-3">
            <div className="flex flex-col gap-1">
              <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
                Current
              </span>
              <span className="break-all text-sm">{currentUser?.email}</span>
            </div>
            <div className="flex items-center gap-2">
              {emailVerified ? (
                <Badge variant="outline" className="border-success/60 text-success">
                  <CheckCircle2 className="h-3 w-3" />
                  Verified
                </Badge>
              ) : (
                <Badge variant="outline" className="border-warning/60 text-warning">
                  <AlertTriangle className="h-3 w-3" />
                  Unverified
                </Badge>
              )}
              {!emailVerified && (
                <Button size="sm" variant="ghost" onClick={resendVerification}>
                  Resend
                </Button>
              )}
            </div>
          </div>
        ) : (
          <div className="flex flex-wrap items-center justify-between gap-3 rounded-md border border-warning/50 bg-warning/5 px-4 py-3">
            <div className="flex flex-col gap-1">
              <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
                Current
              </span>
              <span className="text-sm text-muted-foreground">No email on file</span>
            </div>
            <Badge variant="outline" className="border-warning/60 text-warning">
              <AlertTriangle className="h-3 w-3" />
              Required
            </Badge>
          </div>
        )}

        <form onSubmit={handleUpdate} className="grid gap-4 sm:grid-cols-2">
          <div className="flex flex-col gap-2 sm:col-span-2">
            <Label htmlFor="new-email">New email</Label>
            <Input
              id="new-email"
              type="email"
              autoComplete="email"
              value={newEmail}
              onChange={(e) => setNewEmail(e.target.value)}
              placeholder="you@example.com"
              disabled={loading}
            />
          </div>
          <div className="flex flex-col gap-2 sm:col-span-2">
            <Label htmlFor="email-pwd">Current password</Label>
            <Input
              id="email-pwd"
              type="password"
              autoComplete="current-password"
              value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value)}
              disabled={loading}
            />
            <p className="text-xs text-muted-foreground">Required to confirm it's really you.</p>
          </div>

          {error && (
            <div
              role="alert"
              className="rounded-md border border-destructive/50 bg-destructive/10 px-3 py-2 text-sm text-destructive sm:col-span-2"
            >
              {error}
            </div>
          )}

          <div className="sm:col-span-2">
            <Button type="submit" disabled={loading}>
              {loading ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Mail className="h-4 w-4" />
              )}
              {loading ? 'Sending verification…' : realEmail ? 'Update email' : 'Add email'}
            </Button>
          </div>
        </form>
      </div>
    </Section>
  );
};

const PasswordSection = () => {
  const { currentUser } = useContext(AuthContext);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleUpdate = async (e) => {
    e.preventDefault();
    setError('');
    if (!currentPassword) return setError('Enter your current password.');
    if (newPassword.length < 6) return setError('New password must be at least 6 characters.');
    if (newPassword !== confirmPassword) return setError('New passwords do not match.');

    setLoading(true);
    try {
      const signInEmail = currentUser.email;
      if (!signInEmail) throw new Error('Account is missing a sign-in email.');
      const credential = EmailAuthProvider.credential(signInEmail, currentPassword);
      await reauthenticateWithCredential(currentUser, credential);
      await updatePassword(currentUser, newPassword);
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      toast.success('Password updated.');
    } catch (err) {
      console.error(err);
      setError(handleFirebaseError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Section
      icon={KeyRound}
      kicker="Security"
      title="Password"
      description="Use at least 6 characters. You'll be asked to confirm your current password."
    >
      <form onSubmit={handleUpdate} className="grid gap-4">
        <div className="flex flex-col gap-2">
          <Label htmlFor="cur-pwd">Current password</Label>
          <Input
            id="cur-pwd"
            type="password"
            autoComplete="current-password"
            value={currentPassword}
            onChange={(e) => setCurrentPassword(e.target.value)}
            disabled={loading}
          />
        </div>
        <div className="grid gap-4 sm:grid-cols-2">
          <div className="flex flex-col gap-2">
            <Label htmlFor="new-pwd">New password</Label>
            <Input
              id="new-pwd"
              type="password"
              autoComplete="new-password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              disabled={loading}
            />
          </div>
          <div className="flex flex-col gap-2">
            <Label htmlFor="confirm-pwd">Confirm new password</Label>
            <Input
              id="confirm-pwd"
              type="password"
              autoComplete="new-password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              disabled={loading}
            />
          </div>
        </div>
        {error && (
          <div
            role="alert"
            className="rounded-md border border-destructive/50 bg-destructive/10 px-3 py-2 text-sm text-destructive"
          >
            {error}
          </div>
        )}
        <div>
          <Button type="submit" disabled={loading}>
            {loading ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <KeyRound className="h-4 w-4" />
            )}
            {loading ? 'Updating…' : 'Update password'}
          </Button>
        </div>
      </form>
    </Section>
  );
};

const UserSettings = () => {
  const { currentUser, userProfile } = useContext(AuthContext);
  const realEmail = hasRealEmail(userProfile, currentUser);

  return (
    <div className="mx-auto w-full max-w-screen-md px-4 pb-24 pt-12 sm:px-6">
      <div className="mb-10 flex flex-col gap-3">
        <div className="flex items-center gap-3 font-mono text-[10px] uppercase tracking-[0.3em] text-muted-foreground">
          <span className="h-px w-6 bg-primary" />
          Account
        </div>
        <h1 className="font-display text-3xl font-bold tracking-tight sm:text-4xl">
          Account settings
        </h1>
        <p className="text-sm text-muted-foreground">
          Manage your sign-in email and password. Need to edit your public profile?{' '}
          <Link
            to={currentUser ? `/user/${currentUser.uid}` : '/login'}
            className="text-foreground underline underline-offset-4"
          >
            Open profile
          </Link>
          .
        </p>
      </div>

      {!realEmail && (
        <div
          role="alert"
          className="mb-8 flex items-start gap-3 rounded-md border border-warning/50 bg-warning/10 px-4 py-3 text-sm"
        >
          <ShieldCheck className="mt-0.5 h-4 w-4 shrink-0 text-warning" />
          <div className="flex flex-col gap-1">
            <p className="font-medium text-foreground">Your account has no email address.</p>
            <p className="text-muted-foreground">
              Sign-in currently uses a placeholder ({LEGACY_EMAIL_DOMAIN}). Add your real email
              below so you can recover your account and keep signing in once username login is
              retired.
            </p>
          </div>
        </div>
      )}

      <div className="flex flex-col gap-6">
        <EmailSection />
        <PasswordSection />
      </div>
    </div>
  );
};

export default UserSettings;
